import { Row } from 'react-bootstrap';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import Button from 'components/button/Button';
import AirbnbCancellationPolicyField from 'components/domain/airbnb/AirbnbCancellationPolicyField';
import useFetchAgencyChannelSettings from 'components/domain/channelSettings/useFetchAgencyChannelSettings';
import LeadChannelIcon from 'components/domain/lead/LeadChannelIcon';
import RadioGroupField from 'components/forms/radioGroupField/RadioGroupField';
import SelectField from 'components/forms/selectField/SelectField';
import SwitchRegularField from 'components/forms/switchField/SwitchRegularField';
import TextField from 'components/forms/textField/TextField';
import { AirbnbCheckInOptions } from 'models/AirbnbCheckInOptions';
import { Channel } from 'models/Channels';
import { LeadChannel } from 'models/Leads';
import { PropertyBusinessType } from 'models/Properties';
import { PropertyListingTypes } from 'models/PropertyListingTypes';
import {
  FormSectionColumn,
  SectionTitleContainer,
} from 'pages/property/common/Common.styles';
import PropertyFormFieldLabel from 'pages/property/common/PropertyFormFieldLabel';
import TooltipContentWithLearnMore from 'pages/property/common/tooltip/TooltipContentWithLearnMore';
import usePropertySettingsInitData from 'pages/property/usePropertySettingsInitData';
import { PropertyDetailsCollapseStyled } from '../propertyDetails/PropertyDetails.styles';
import { PropertySettingsMainTabFormValues } from '../../PropertySettingsMainTab.schema';
import { AirbnbPromotionStatus } from '../../PropertySettingsMainTab.types';
import {
  NonRefundableRateDiscountContainer,
  OpenSetInstructionsContainer,
  SwitchContainer,
  TightFormSectionColumn,
  ViewAirbnbResolutionsStyled,
} from './AirbnbSettings.styles';
import NonRefundableRateDiscountField from './NonRefundableRateDiscountField';
import AllowBookingRequestField from './AllowBookingRequestField';
import SendMessageAsField from './SendMessageAsField';
import GuestRequirementsField from './GuestRequirementsField';
import BookingTypeField from './BookingTypeField';
import { checkGuestRequirementsFieldVisibility } from './AirbnbSettings.utils';
import CheckInTimeEndField from './CheckInTimeEndField';
import CheckInTimeStartField from './CheckInTimeStartField';
import useAirbnbCancellationPolicyOptions from './useAirbnbCancellationPolicyOptions';
import useSetInstructionsModal from './useSetInstructionsModal';

const AirbnbSettings = () => {
  const { t } = useTranslation();
  const { getCopyAddonIcon } = useCopyToClipboard();
  const { property } = usePropertySettingsInitData();
  const { openSetInstructionsModal } = useSetInstructionsModal();
  const {
    // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    airbnbSettings: { airbnbId, promotions },
  } = useWatch<PropertySettingsMainTabFormValues>();

  const { channelSettings, isFetching: isLoadingChannelSettings } =
    useFetchAgencyChannelSettings(true);

  const cancellationPolicyOptions = useAirbnbCancellationPolicyOptions(
    property?.uid,
  );

  const isAirbnbActive = !!channelSettings?.find(
    ({ enumId }) => enumId === Channel.airbnb,
  )?.isActive;

  const isHotel = property?.businessType === PropertyBusinessType.HOTEL;

  if (!isAirbnbActive || isLoadingChannelSettings || isHotel) {
    return null;
  }

  const listingTypeOptions = [
    PropertyListingTypes.ENTIREHOME,
    PropertyListingTypes.SHAREDROOM,
    PropertyListingTypes.PRIVATEROOM,
  ].map((value) => ({
    label: t(
      `pageProperty.mainSettings.airbnbSettings.listingTypeOptions.${value}`,
    ),
    value,
  }));

  const checkInOptions = Object.values(AirbnbCheckInOptions).map((value) => ({
    label: t(
      `pageProperty.mainSettings.airbnbSettings.checkInOptionOptions.${value}`,
    ),
    value,
  }));

  const nonRefundableRateOptions = [
    { label: t('common.disabled'), value: 'false' },
    { label: t('common.enabled'), value: 'true' },
  ];

  const isPromotionExpired =
    promotions?.[0]?.status === AirbnbPromotionStatus.EXPIRED;

  const promotionOptions = [
    {
      label: t('common.disabled'),
      value: isPromotionExpired
        ? AirbnbPromotionStatus.EXPIRED
        : AirbnbPromotionStatus.AVAILABLE,
    },
    { label: t('common.enabled'), value: AirbnbPromotionStatus.ONGOING },
  ];

  const isStandaloneProperty =
    property?.businessType === PropertyBusinessType.STANDALONE_PROPERTY;
  const isUnitType = property?.businessType === PropertyBusinessType.UNIT_TYPE;
  const isSubUnit = property?.businessType === PropertyBusinessType.SUB_UNIT;
  const isGuestRequirementsFieldVisible =
    checkGuestRequirementsFieldVisibility(property);
  const isCancellationPolicyFieldVisible =
    isStandaloneProperty || isSubUnit || isUnitType;
  const isCheckInOptionFieldVisible = isStandaloneProperty || isSubUnit;
  const isNonRefundableRateFieldVisible = isStandaloneProperty || isSubUnit;
  const isViewAirbnbResolutionsVisible = isStandaloneProperty || isSubUnit;
  const isTopRatedGuestDiscountVisible =
    isStandaloneProperty || isSubUnit || isUnitType;
  const isMobileOnlyDiscountVisible =
    isStandaloneProperty || isSubUnit || isUnitType;
  const isPromotionFieldVisible = promotions?.length > 0;

  const sectionTitle = (
    <SectionTitleContainer>
      <LeadChannelIcon channel={LeadChannel.AIRBNB} />
      {t('pageProperty.mainSettings.airbnbSettings.sectionTitle')}
    </SectionTitleContainer>
  );

  return (
    <PropertyDetailsCollapseStyled
      data-testid="airbnb-settings"
      defaultExpanded
      label={sectionTitle}
      variant="card"
    >
      <fieldset>
        <Row>
          <FormSectionColumn>
            <TextField
              label={t('pageProperty.mainSettings.airbnbSettings.airbnbId')}
              name="airbnbSettings.airbnbId"
              colSmInput={7}
              colSmLabel={5}
              disabled
              rightAddons={[getCopyAddonIcon(airbnbId)]}
            />
            <SelectField
              label={t('pageProperty.mainSettings.airbnbSettings.listingType')}
              name="airbnbSettings.listingType"
              options={listingTypeOptions}
              colSmInput={7}
              colSmLabel={5}
              required
            />
            <BookingTypeField />
            <AllowBookingRequestField />
            {isGuestRequirementsFieldVisible && <GuestRequirementsField />}
            {isCancellationPolicyFieldVisible && (
              <AirbnbCancellationPolicyField
                name="airbnbSettings.cancellationPolicy"
                label={
                  <PropertyFormFieldLabel name="airbnbSettings.cancellationPolicy">
                    {t(
                      'pageProperty.mainSettings.airbnbSettings.cancellationPolicy',
                    )}
                  </PropertyFormFieldLabel>
                }
                options={cancellationPolicyOptions}
                required
              />
            )}
          </FormSectionColumn>
          <TightFormSectionColumn>
            <SendMessageAsField />
            <CheckInTimeStartField />
            <CheckInTimeEndField />
            {isCheckInOptionFieldVisible && (
              <>
                <SelectField
                  label={t(
                    'pageProperty.mainSettings.airbnbSettings.checkInOption',
                  )}
                  name="airbnbSettings.checkInOption"
                  options={checkInOptions}
                  colSmInput={7}
                  colSmLabel={5}
                />
                <OpenSetInstructionsContainer sm={7} smOffset={5}>
                  <Button
                    bsStyle="link"
                    data-testid="open-set-instructions-modal"
                    onClick={openSetInstructionsModal}
                  >
                    {t(
                      'pageProperty.mainSettings.airbnbSettings.setInstructions',
                    )}
                  </Button>
                </OpenSetInstructionsContainer>
              </>
            )}
            {isNonRefundableRateFieldVisible && (
              <>
                <RadioGroupField
                  label={
                    <PropertyFormFieldLabel name="airbnbSettings.nonRefundableRateEnabled">
                      {t(
                        'pageProperty.mainSettings.airbnbSettings.nonRefundableRate',
                      )}
                    </PropertyFormFieldLabel>
                  }
                  name="airbnbSettings.nonRefundableRateEnabled"
                  options={nonRefundableRateOptions}
                  colSmInput={7}
                  colSmLabel={5}
                  horizontal
                />
                <NonRefundableRateDiscountContainer
                  xs={8}
                  xsOffset={4}
                  sm={7}
                  smOffset={5}
                >
                  <NonRefundableRateDiscountField />
                </NonRefundableRateDiscountContainer>
              </>
            )}

            {isPromotionFieldVisible && (
              <RadioGroupField
                label={
                  <PropertyFormFieldLabel name="airbnbSettings.promotions[0].status">
                    {t('pageProperty.mainSettings.airbnbSettings.promotions')}
                  </PropertyFormFieldLabel>
                }
                name="airbnbSettings.promotions[0].status"
                options={promotionOptions}
                colSmInput={7}
                colSmLabel={5}
                disabled={isPromotionExpired}
                horizontal
                rightTooltip={
                  <TooltipContentWithLearnMore
                    labelTooltipText={t(
                      'pageProperty.mainSettings.airbnbSettings.promotionsTooltip',
                    )}
                    learnMoreUrl="https://help.hostfully.com/en/articles/9414601-send-special-offers-or-give-discounts-to-airbnb-guests"
                  />
                }
              />
            )}

            {isViewAirbnbResolutionsVisible && (
              <ViewAirbnbResolutionsStyled
                data-testid="view-airbnb-resolutions"
                to="/channels/airbnb/resolutions"
                target="_blank"
                rel="noreferrer"
              >
                {t(
                  'pageProperty.mainSettings.airbnbSettings.viewAirbnbResolutions',
                )}
              </ViewAirbnbResolutionsStyled>
            )}

            {isMobileOnlyDiscountVisible && (
              <SwitchContainer>
                <SwitchRegularField
                  name="airbnbSettings.mobileOnlyDiscountEnabled"
                  label={
                    <PropertyFormFieldLabel name="airbnbSettings.mobileOnlyDiscountEnabled">
                      {t(
                        'pageProperty.mainSettings.airbnbSettings.mobileDiscount',
                      )}
                    </PropertyFormFieldLabel>
                  }
                  colSmLabel={5}
                  colSmInput={7}
                  rightTooltip={
                    <TooltipContentWithLearnMore
                      labelTooltipText={t(
                        'pageProperty.mainSettings.airbnbSettings.mobileDiscountTooltip',
                      )}
                      learnMoreUrl="https://help.hostfully.com/en/articles/9414601-send-special-offers-or-give-discounts-to-airbnb-guests"
                    />
                  }
                />
              </SwitchContainer>
            )}

            {isTopRatedGuestDiscountVisible && (
              <SwitchContainer>
                <SwitchRegularField
                  name="airbnbSettings.topRatedGuestDiscountEnabled"
                  label={
                    <PropertyFormFieldLabel name="airbnbSettings.topRatedGuestDiscountEnabled">
                      {t(
                        'pageProperty.mainSettings.airbnbSettings.topRatedGuestDiscount',
                      )}
                    </PropertyFormFieldLabel>
                  }
                  colSmLabel={5}
                  colSmInput={7}
                  rightTooltip={
                    <TooltipContentWithLearnMore
                      labelTooltipText={t(
                        'pageProperty.mainSettings.airbnbSettings.topRatedGuestDiscountTooltip',
                      )}
                      learnMoreUrl="https://help.hostfully.com/en/articles/9414601-send-special-offers-or-give-discounts-to-airbnb-guests"
                    />
                  }
                />
              </SwitchContainer>
            )}
          </TightFormSectionColumn>
        </Row>
      </fieldset>
    </PropertyDetailsCollapseStyled>
  );
};

export default AirbnbSettings;
